import { Component } from '@theme/component';

/**
 * Text counts and optional personalization linked to real, priced Shopify variants.
 * Native variant controls remain available until their replacement can be connected.
 */
class ProductCustomProperty extends Component {
  #controller;
  #observer;
  #root;
  #scheduled = false;

  connectedCallback() {
    super.connectedCallback();
    this.#connectOption();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connectOption();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = undefined;
    this.#observer = undefined;
    super.disconnectedCallback();
  }

  #connectOption() {
    const config = this.querySelector('[data-coco-option]');
    if (!config?.dataset.cocoOption) return;
    if (!this.#controller) {
      this.#root = this.closest('[id^="shopify-section-"]');
      if (!this.#root) return;
      this.#controller = new AbortController();
      this.#root.addEventListener('change', (event) => this.#onChange(event), {
        signal: this.#controller.signal,
      });
      this.#observer = new MutationObserver(() => this.#schedule());
      this.#observer.observe(this.#root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'data-current-checked'],
      });
    }
    this.#schedule();
  }

  #schedule() {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      if (this.isConnected) this.#sync();
    });
  }

  #controls() {
    const config = this.querySelector('[data-coco-option]');
    const field = this.querySelector('[data-coco-conditional-content]');
    if (!config || !field || !this.#root) return null;
    const inputs = Array.from(this.#root.querySelectorAll('variant-picker input[type="radio"][data-option-name]'))
      .filter((input) => input.dataset.optionName === config.dataset.cocoOption);
    const active = inputs.find((input) => input.value === config.dataset.cocoActive);
    if (!active) return null;
    const standard = inputs.find((input) => input.value === config.dataset.cocoStandard);
    const toggle = this.querySelector('[data-coco-addon-toggle]');
    const trigger = Array.from(this.#root.querySelectorAll('select[name]')).find(
      (input) => input.name === `properties[${config.dataset.cocoTriggerKey}]`
    );
    return { config, field, inputs, active, standard, toggle, trigger };
  }

  #onChange(event) {
    const controls = this.#controls();
    if (!controls) return;
    const { config, active, standard, toggle, trigger } = controls;
    if (event.target === toggle && toggle instanceof HTMLInputElement) {
      const target = toggle.checked ? active : standard;
      if (!target || target.disabled) {
        this.#sync();
        return;
      }
      // A custom monogram must never remain selected with the standard-price variant.
      if (!toggle.checked && trigger instanceof HTMLSelectElement && trigger.value === config.dataset.cocoTriggerValue) {
        trigger.value = '';
        trigger.dispatchEvent(new Event('change', { bubbles: true }));
      }
      target.click();
    } else if (event.target === trigger && trigger?.value === config.dataset.cocoTriggerValue && !active.checked) {
      active.click();
    }
    this.#schedule();
  }

  #sync() {
    const controls = this.#controls();
    if (!controls) return;
    const { field, inputs, active, standard, toggle } = controls;
    const enabled = active.checked;
    if (toggle instanceof HTMLInputElement && standard && inputs.length === 2) {
      toggle.checked = enabled;
      toggle.disabled = Boolean((enabled ? standard : active).disabled);
      toggle.closest('label').hidden = false;
      // Keep the actual priced controls inside the native picker for price/stock resolution.
      active.closest('fieldset')?.setAttribute('data-coco-addon-hidden', '');
    }
    field.hidden = !enabled;
    for (const input of field.querySelectorAll('input[name], textarea[name], select[name]')) {
      if (!input.name.startsWith('properties[')) continue;
      if (!input.hasAttribute('data-coco-required')) input.dataset.cocoRequired = String(input.required);
      input.disabled = !enabled;
      input.required = enabled && input.dataset.cocoRequired === 'true';
    }
  }

  handleInput() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;
    const template = characterCount.getAttribute('data-template');
    if (!template) return;
    characterCount.textContent = template
      .replace('[current]', textInput.value.length.toString())
      .replace('[max]', textInput.maxLength.toString());
  }
}

if (!customElements.get('product-custom-property-component')) {
  customElements.define('product-custom-property-component', ProductCustomProperty);
}
